import { validateEmailHelper } from "util/helpers"
import { ICreateUserApiPayload, IUpdateUserApiPayload, IUserListApiItem } from "models/axiosModel"

export type ConfigurationUsernameType = "email" | "phone"

export type ConfigurationUserRoleName = "Admin" | "User"

export type ConfigurationUser = {
    private_user_id: number
    first_name: string
    last_name: string
    username_type: ConfigurationUsernameType
    email: string
    phone: string
    role_name: ConfigurationUserRoleName
    flag_active: boolean
    last_login_at: string | null
    updated_by_user_name: string
}

export type UserListRow = ConfigurationUser & { user_display: string; username: string }

export const CONFIGURATION_ROLE_ID_BY_NAME: Record<ConfigurationUserRoleName, number> = {
    Admin: 1,
    User: 2,
}

export const CONFIGURATION_ROLE_NAME_BY_ID: Record<number, ConfigurationUserRoleName> = {
    1: "Admin",
    2: "User",
}

export const CONFIGURATION_ROLE_OPTIONS: { label: string; value: string }[] = [
    { label: "Admin", value: "Admin" },
    { label: "User", value: "User" },
]

export const USER_FILTER_ACCESSORS = {
    role: (row: ConfigurationUser) => row.role_name,
    status: (row: ConfigurationUser) => (row.flag_active ? "Active" : "Inactive"),
}

export const USER_FILTER_GROUPS = [
    { key: "role", label: "Role", options: CONFIGURATION_ROLE_OPTIONS },
    {
        key: "status",
        label: "Status",
        options: [
            { label: "Active", value: "Active" },
            { label: "Inactive", value: "Inactive" },
        ],
    },
]

export const displayUserName = (user: Pick<ConfigurationUser, "first_name" | "last_name">): string =>
    `${user.first_name} ${user.last_name}`.trim()

export const loginIdentifier = (user: Pick<ConfigurationUser, "username_type" | "email" | "phone">): string =>
    user.username_type === "phone" ? user.phone : user.email

export const toUserListRow = (row: ConfigurationUser): UserListRow => ({
    ...row,
    user_display: displayUserName(row),
    username: loginIdentifier(row),
})

export const userSearchFields = [
    (r: ConfigurationUser) => r.first_name,
    (r: ConfigurationUser) => r.last_name,
    (r: ConfigurationUser) => displayUserName(r),
    (r: ConfigurationUser) => loginIdentifier(r),
    (r: ConfigurationUser) => r.updated_by_user_name,
]

export const isSelfConfigurationUser = (
    user: Pick<ConfigurationUser, "private_user_id">,
    loggedInUserId: number | null | undefined,
): boolean => loggedInUserId != null && user.private_user_id === loggedInUserId

export const mapUserListItemToConfigurationUser = (item: IUserListApiItem): ConfigurationUser => ({
    private_user_id: item.private_user_id,
    first_name: item.user_first_name,
    last_name: item.user_last_name,
    username_type: item.user_mobile_number ? "phone" : "email",
    email: item.user_email || "",
    phone: item.user_mobile_number || "",
    role_name: (CONFIGURATION_ROLE_NAME_BY_ID[item.user_role_id] ?? item.user_role_name) as ConfigurationUserRoleName,
    flag_active: item.flag_active,
    last_login_at: item.last_login_timestamp ?? null,
    updated_by_user_name: item.updated_by_user_name || item.created_by_user_name,
})

export const buildCreateUserPayload = (fields: {
    firstName: string
    lastName: string
    usernameType: ConfigurationUsernameType
    email: string
    phone: string
    password: string
    roleName: ConfigurationUserRoleName
    flagActive: boolean
}): ICreateUserApiPayload => ({
    user_first_name: fields.firstName.trim(),
    user_last_name: fields.lastName.trim() || undefined,
    user_email: fields.usernameType === "email" ? fields.email.trim() : undefined,
    user_mobile_number: fields.usernameType === "phone" ? fields.phone.trim() : undefined,
    user_password: fields.password,
    user_role_id: CONFIGURATION_ROLE_ID_BY_NAME[fields.roleName],
    flag_active: fields.flagActive,
})

export const buildUpdateUserPayload = (
    user: ConfigurationUser,
    fields: { firstName: string; lastName: string; email: string; phone: string; roleName: ConfigurationUserRoleName; flagActive: boolean },
): IUpdateUserApiPayload => ({
    private_user_id: user.private_user_id,
    user_first_name: fields.firstName.trim(),
    user_last_name: fields.lastName.trim() || undefined,
    user_email: user.username_type === "email" ? fields.email.trim() : undefined,
    user_mobile_number: user.username_type === "phone" ? fields.phone.trim() : undefined,
    user_role_id: CONFIGURATION_ROLE_ID_BY_NAME[fields.roleName],
    flag_active: fields.flagActive,
})

export const sanitizePhone = (value: string): string => value.replace(/\D/g, "").slice(0, 10)

export const nameError = (value: string, label: string): string | undefined =>
    value.trim() ? undefined : `${label} is required`

export const identifierError = (
    usernameType: ConfigurationUsernameType,
    email: string,
    phone: string,
): string | undefined => {
    if (usernameType === "email") {
        if (!email.trim()) return "Email is required"
        if (!validateEmailHelper(email.trim())) return "Please enter a valid email address"
        return undefined
    }
    if (!phone.trim()) return "Phone number is required"
    if (phone.trim().length !== 10) return "Please enter a valid 10 digit phone number"
    return undefined
}

export const passwordError = (password: string): string | undefined => {
    if (!password) return "Password is required"
    if (password.length < 6) return "Password must be at least 6 characters"
    return undefined
}

export const newPasswordError = (password: string, confirm: string): string | undefined => {
    if (!password || !confirm) return "Enter and confirm the new password"
    if (password.length < 6) return "Password must be at least 6 characters"
    if (password !== confirm) return "Passwords do not match"
    return undefined
}
